import { useMemo, useState } from 'react';
import { Button, Flex, Modal, Typography, Upload, message } from 'antd';
import type { UploadFile } from 'antd/es/upload/interface';
import { File, FileArrowUp, Printer, Tag } from '@phosphor-icons/react';
import { useGlobalContext } from '@contexts/global';
import type { WmsShipmentInboundType } from 'schemas/shipment/inbound/detail';
import { getInboundShipmentWarehouseId } from '@utility/utils/shipment-display';
import { COLOR } from '@styles/theme';
import { ShipmentsService, ToastMessage } from '@utility/index';
import { UploadService } from '@utility/services/upload';
import { InboundCreateLicensePlatesModal } from './InboundCreateLicensePlatesModal';
import { InboundGeneratePalletLabelsModal } from './InboundGeneratePalletLabelsModal';

const { Text } = Typography;

type InboundShipmentDocumentsTabProps = {
  data: WmsShipmentInboundType;
  onRefresh: () => void;
};

function getFileExtension(fileName: string) {
  const parts = fileName.split('.');
  return parts.length > 1 ? parts.pop()!.toLowerCase() : '';
}

export function InboundShipmentDocumentsTab({
  data,
  onRefresh,
}: InboundShipmentDocumentsTabProps) {
  const { warehouseId: globalWarehouseId } = useGlobalContext();
  const documents = data.documents ?? [];
  const [modalOpen, setModalOpen] = useState(false);
  const [licensePlatesModalOpen, setLicensePlatesModalOpen] = useState(false);
  const [generateLabelsModalOpen, setGenerateLabelsModalOpen] = useState(false);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [uploading, setUploading] = useState(false);

  const warehouseId = useMemo(
    () => getInboundShipmentWarehouseId(data, globalWarehouseId),
    [data, globalWarehouseId],
  );

  const shipmentPallets = useMemo(
    () => (data.pallets ?? []).filter(p => p.palletNumber),
    [data.pallets],
  );

  const hasShipmentPallets = shipmentPallets.length > 0;

  const resetModal = () => {
    setFileList([]);
  };

  const handleUpload = async () => {
    const file = fileList[0]?.originFileObj;
    const extension = file ? getFileExtension(file.name) : '';

    if (!data._id) return;
    if (!file || !extension) {
      ToastMessage.error('Select a file to upload.');
      return;
    }

    try {
      setUploading(true);
      const signed = await ShipmentsService.getDocumentUploadUrl({
        shipmentId: data._id,
        extension,
      });

      if (!signed?.uploadUrl || !signed?.key || !signed?.bucketName) {
        ToastMessage.error('Unable to prepare upload.');
        return;
      }

      await UploadService.media(signed.uploadUrl, file, file.type);

      await ShipmentsService.uploadShipmentDocuments({
        shipmentId: data._id,
        documents: [
          {
            key: signed.key,
            fileName: file.name,
            fileType: file.type,
            bucketName: signed.bucketName,
            documentType: extension,
            fileSize: file.size,
          },
        ],
      });

      ToastMessage.success('Document uploaded');
      setModalOpen(false);
      resetModal();
      onRefresh();
    } catch (error) {
      ToastMessage.apiError(error);
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = (doc: (typeof documents)[number]) => {
    if (doc.filePath) {
      window.open(doc.filePath, '_blank', 'noopener,noreferrer');
      return;
    }
    message.info(`Download ${doc.fileName || doc.documentType}`);
  };

  return (
    <Flex vertical gap={16} style={{ padding: '8px 0 0' }}>
      {documents.length === 0 ? (
        <Text type="secondary" style={{ fontSize: 14 }}>
          No documents yet.
        </Text>
      ) : (
        <Flex vertical gap={12}>
          {documents.map(doc => (
            <Flex
              key={doc._id}
              vertical
              gap={12}
              style={{
                padding: 16,
                border: `1px solid ${COLOR.border}`,
                borderRadius: 6,
              }}
            >
              <Flex align="center" gap={10}>
                <File size={24} color={COLOR.black} />
                <Flex vertical gap={2} style={{ minWidth: 0 }}>
                  <Text strong style={{ fontSize: 14 }}>
                    {doc.fileName || doc.documentType || 'Document'}
                  </Text>
                  {doc.documentType && doc.fileName ? (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {doc.documentType.replace(/_/g, ' ')}
                    </Text>
                  ) : null}
                </Flex>
              </Flex>
              <Button block onClick={() => handleDownload(doc)}>
                Download
              </Button>
            </Flex>
          ))}
        </Flex>
      )}

      <Button
        block
        icon={<Tag size={16} />}
        disabled={!warehouseId || !data._id}
        onClick={() => setLicensePlatesModalOpen(true)}
        title={
          !warehouseId ? 'Warehouse is required to create licence plates' : undefined
        }
      >
        Create licence plates
      </Button>

      {hasShipmentPallets ? (
        <Button
          block
          icon={<Printer size={16} />}
          disabled={!warehouseId}
          onClick={() => setGenerateLabelsModalOpen(true)}
          title={!warehouseId ? 'Warehouse is required to generate labels' : undefined}
        >
          Generate labels
        </Button>
      ) : null}

      <Button
        block
        icon={<FileArrowUp size={16} />}
        onClick={() => setModalOpen(true)}
        style={{ marginTop: documents.length ? 0 : 8 }}
      >
        Add Document
      </Button>

      <InboundCreateLicensePlatesModal
        open={licensePlatesModalOpen}
        onClose={() => setLicensePlatesModalOpen(false)}
        shipmentId={data._id}
        warehouseId={warehouseId}
        onCompleted={onRefresh}
      />

      <InboundGeneratePalletLabelsModal
        open={generateLabelsModalOpen}
        onClose={() => setGenerateLabelsModalOpen(false)}
        warehouseId={warehouseId}
        pallets={shipmentPallets}
        onCompleted={onRefresh}
      />

      <Modal
        title="Add Document"
        open={modalOpen}
        centered
        onCancel={() => {
          if (uploading) return;
          setModalOpen(false);
          resetModal();
        }}
        onOk={handleUpload}
        okText="Upload"
        confirmLoading={uploading}
        destroyOnHidden
      >
        <Upload
          beforeUpload={() => false}
          maxCount={1}
          fileList={fileList}
          onChange={({ fileList: next }) => setFileList(next)}
          disabled={uploading}
        >
          <Button disabled={uploading}>Choose file</Button>
        </Upload>
      </Modal>
    </Flex>
  );
}
